/**
 * Sammelt die Messwerte des Roboters fuer die Anzeige (W12).
 *
 * Die Reihen liegen bewusst ausserhalb des React-Zustands: bei 100 Messpunkten
 * je Sekunde wuerde ein neuer Zustand je Punkt die Oberflaeche mehr mit
 * Neuzeichnen beschaeftigen als mit Anzeigen. Stattdessen sammelt ein Objekt
 * die Werte, und ein Takt von 10 Bildern je Sekunde loest das Neuzeichnen aus.
 *
 * Die Quelle ist heute die serielle Verbindung. Damit spaeter BLE oder WLAN
 * dieselbe Anzeige speisen koennen, haengt dieser Baustein nur am Ereignis
 * "telemetry" und nicht am Uebertragungsweg.
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDevice } from "../device/device-hooks";
import { PicoDeviceConnection } from "../device/pico/connection";
import type { RemoteControl, RemoteValue, TelemetrySample } from "./parse";
import { TelemetrySeries } from "./series";

/** So oft wird neu gezeichnet, unabhaengig von der Datenrate. */
const REDRAW_INTERVAL_MS = 100;

/**
 * Abstand zweier Lebenszeichen an den Roboter.
 *
 * Die Frist dort betraegt zwei Sekunden (``remote.timeout``); vier
 * ausgefallene Lebenszeichen duerfen damit vorkommen, bevor sie greift.
 *
 * Ein Lebenszeichen traegt den **vollstaendigen Zustand** aller
 * Bedienelemente, nicht einen leeren Satz. Das kostet nichts - die Werte sind
 * ohnehin da - und macht den Stillstand zum Ruhezustand: geht eine Aenderung
 * unterwegs verloren, steht spaetestens eine halbe Sekunde spaeter wieder der
 * richtige Wert im Roboter. Niemand muss etwas nachtippen.
 *
 * Die erste Fassung schickte einen leeren Satz. Sie hielt die Sitzung am
 * Leben, ohne je etwas zu berichtigen - und nahm damit der Frist genau den
 * Fall weg, fuer den sie gebaut war: eine verlorene ``[0, 0]`` liess den
 * Roboter unbegrenzt weiterfahren, weil das Lebenszeichen dem Roboter
 * versicherte, es sei alles in Ordnung.
 */
const HEARTBEAT_INTERVAL_MS = 500;

interface TelemetryContextValue {
  series: TelemetrySeries;
  /** Bedienelemente, die das laufende Programm angemeldet hat. */
  controls: RemoteControl[];
  /** Aktuelle Werte der Bedienelemente, wie in der Oberflaeche eingestellt. */
  controlValues: Record<string, RemoteValue>;
  /** Setzt einen Wert und schickt ihn an den Roboter. */
  setControlValue: (name: string, value: RemoteValue) => void;
  /** Zaehlt bei jedem Neuzeichnen hoch; Anzeigen haengen sich daran. */
  revision: number;
  recording: boolean;
  setRecording: (value: boolean) => void;
  clear: () => void;
}

const TelemetryContext = createContext<TelemetryContextValue | undefined>(
  undefined
);

export const TelemetryProvider = ({ children }: { children: ReactNode }) => {
  const device = useDevice();
  const seriesRef = useRef(new TelemetrySeries());
  const [revision, setRevision] = useState(0);
  const [recording, setRecording] = useState(true);
  const [controls, setControls] = useState<RemoteControl[]>([]);
  const [controlValues, setControlValues] = useState<
    Record<string, RemoteValue>
  >({});
  const recordingRef = useRef(recording);
  recordingRef.current = recording;

  useEffect(() => {
    if (!(device instanceof PicoDeviceConnection)) {
      return;
    }
    const series = seriesRef.current;
    const listener = ({ sample }: { sample: TelemetrySample }) => {
      if (recordingRef.current) {
        series.add(sample);
      }
    };
    // Eine Anmeldung ersetzt die bisherigen Bedienelemente vollstaendig:
    // startet ein anderes Programm, sind die alten gegenstandslos.
    const controlListener = ({
      controls: neu,
    }: {
      controls: RemoteControl[];
    }) => {
      setControls(neu);
      const werte: Record<string, RemoteValue> = {};
      for (const control of neu) {
        if (control.kind === "button") {
          continue;
        }
        if (
          typeof control.value === "number" ||
          typeof control.value === "boolean" ||
          Array.isArray(control.value)
        ) {
          werte[control.name] = control.value;
        } else if (control.kind === "switch") {
          werte[control.name] = false;
        } else if (control.kind === "joystick") {
          // Ruhelage, nicht ein Ausschlag. Ein Joystick, der beim Anmelden
          // irgendwo steht, faehrt los, ohne dass jemand ihn angefasst hat.
          werte[control.name] = [0, 0];
        } else {
          werte[control.name] = control.min ?? 0;
        }
      }
      setControlValues(werte);
      // Dasselbe fuer das, was wiederholt wird. Ein neues Programm
      // bringt neue Bedienelemente mit; die alten Werte sind
      // gegenstandslos und duerfen nicht weiter gesendet werden.
      zustandRef.current = { ...werte };
      ereignisseRef.current = {};
    };
    device.addEventListener("telemetry", listener);
    device.addEventListener("remotecontrols", controlListener);
    return () => {
      device.removeEventListener("telemetry", listener);
      device.removeEventListener("remotecontrols", controlListener);
    };
  }, [device]);

  useEffect(() => {
    const handle = setInterval(
      () => setRevision((value) => value + 1),
      REDRAW_INTERVAL_MS
    );
    return () => clearInterval(handle);
  }, []);

  // Der Zustand, der wiederholt wird: Joystick, Schieberegler, Schalter.
  const zustandRef = useRef<Record<string, RemoteValue>>({});
  // Was genau einmal gilt. Ein Tastendruck ist ein Ereignis und kein Zustand;
  // wiederholt man ihn alle 500 ms mit, loest der Knopf endlos wieder aus.
  const ereignisseRef = useRef<Record<string, RemoteValue>>({});
  // Hoechstens eine Nachricht unterwegs. Beim Ziehen am Joystick entstehen bis
  // zu 120 Aenderungen je Sekunde - mehr, als die Funkstrecke traegt. Statt sie
  // alle loszuschicken, wartet die naechste, und was in der Zwischenzeit
  // passiert, geht als **neuester** Wert mit. Alte Zwischenstaende sind
  // ohnehin wertlos: niemand will wissen, wo der Joystick vor 50 ms stand.
  const laeuftRef = useRef(false);
  const erneutRef = useRef(false);

  const senden = useCallback(async () => {
    if (!(device instanceof PicoDeviceConnection)) {
      return;
    }
    if (laeuftRef.current) {
      erneutRef.current = true;
      return;
    }
    laeuftRef.current = true;
    try {
      do {
        erneutRef.current = false;
        const ereignisse = ereignisseRef.current;
        ereignisseRef.current = {};
        try {
          await device.sendRemote({ ...zustandRef.current, ...ereignisse });
        } catch (fehler) {
          // Tastendruecke zurueck in die Schlange: ein Knopf, den jemand
          // gedrueckt hat, soll nicht daran scheitern, dass gerade die
          // Funkstrecke stolperte. Werte brauchen das nicht - der naechste
          // Durchgang traegt sie ohnehin.
          ereignisseRef.current = { ...ereignisse, ...ereignisseRef.current };
          // Sichtbar machen. Vorher rief der Rueckkanal ohne catch auf, und ein
          // abgewiesener Schreibvorgang verschwand spurlos.
          console.warn("Bedienwerte nicht angekommen:", fehler);
          return;
        }
      } while (erneutRef.current);
    } finally {
      laeuftRef.current = false;
    }
  }, [device]);

  // Lebenszeichen, solange das Programm Bedienelemente angemeldet hat.
  //
  // Warum es das braucht: die Oberflaeche schickt sonst nur bei Aenderung, und
  // "seit einer Sekunde kam nichts" waere damit der Normalfall. Der Roboter
  // koennte einen Abriss nicht von Stillstand unterscheiden und wuerde mit dem
  // letzten Joystickausschlag weiterfahren.
  useEffect(() => {
    if (controls.length === 0 || !(device instanceof PicoDeviceConnection)) {
      return;
    }
    const handle = setInterval(() => void senden(), HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [device, controls.length, senden]);

  const setControlValue = (name: string, value: RemoteValue) => {
    setControlValues((bisher) => ({ ...bisher, [name]: value }));
    if (controls.some((c) => c.name === name && c.kind === "button")) {
      ereignisseRef.current[name] = value;
    } else {
      zustandRef.current[name] = value;
    }
    void senden();
  };

  const value: TelemetryContextValue = {
    series: seriesRef.current,
    controls,
    controlValues,
    setControlValue,
    revision,
    recording,
    setRecording,
    clear: () => {
      seriesRef.current.clear();
      setRevision((v) => v + 1);
    },
  };
  return (
    <TelemetryContext.Provider value={value}>
      {children}
    </TelemetryContext.Provider>
  );
};

export const useTelemetry = (): TelemetryContextValue => {
  const value = useContext(TelemetryContext);
  if (!value) {
    throw new Error("useTelemetry braucht einen TelemetryProvider");
  }
  return value;
};
